import { quoteCalculator } from './QuoteCalculator'
import { quoteMargin } from './QuoteMargin'
import { quoteValidator } from './QuoteValidator'
import type {
  ComparisonProductSource,
  QuoteBlock,
  QuoteBlockType,
  QuoteDocumentPro,
  QuoteItemOrigin,
} from './QuoteTypes'
import { SERVICE_CATALOG } from './QuoteTypes'

/**
 * QuoteEngine — create/mutate blocks with mandatory origin tracking.
 * Products can only enter via ComparisonProductSource (no free text).
 */
export class QuoteEngine {
  createDocument(title = 'Novo Orçamento PRO'): QuoteDocumentPro {
    const now = new Date().toISOString()
    return {
      id: crypto.randomUUID(),
      customerId: null,
      title,
      status: 'DRAFT',
      globalMarginPercent: null,
      globalDiscountPercent: 0,
      globalDiscountValue: 0,
      blocks: [],
      totals: emptyTotals(),
      createdAt: now,
      updatedAt: now,
    }
  }

  recompute(doc: QuoteDocumentPro): QuoteDocumentPro {
    const blocks = doc.blocks
      .map((b, index) => quoteCalculator.recalculateBlock({ ...b, position: index }))
      .sort((a, b) => a.position - b.position)

    let working = blocks
    if (doc.globalMarginPercent != null) {
      working = quoteMargin.applyGlobalMargin(working, doc.globalMarginPercent)
      working = working.map((b) => quoteCalculator.recalculateBlock(b))
    }

    return {
      ...doc,
      blocks: working,
      totals: quoteCalculator.totals(
        working,
        doc.globalDiscountPercent,
        doc.globalDiscountValue,
      ),
      updatedAt: new Date().toISOString(),
    }
  }

  /** Only allowed path for commercial products. */
  addFromComparison(doc: QuoteDocumentPro, source: ComparisonProductSource): QuoteDocumentPro {
    if (!source.productId) {
      throw new Error('Produto sem ProductID — origem inválida')
    }
    if (!source.fingerprint) {
      throw new Error('Produto sem Fingerprint — use o Comparison Engine')
    }

    const block = this.createBlock({
      type: 'product',
      description: source.description,
      quantity: 1,
      purchasePrice: source.purchasePrice,
      salePrice: source.salePrice,
      origin: {
        source: 'comparison',
        productId: source.productId,
        fingerprint: source.fingerprint,
        supplierId: source.supplierId,
        supplierName: source.supplierName,
        comparisonOfferId: source.comparisonOfferId,
      },
      position: doc.blocks.length,
    })

    return this.recompute({ ...doc, blocks: [...doc.blocks, block] })
  }

  addService(doc: QuoteDocumentPro, serviceId: string): QuoteDocumentPro {
    const service = SERVICE_CATALOG.find((s) => s.id === serviceId)
    if (!service) throw new Error(`Serviço não encontrado: ${serviceId}`)

    const block = this.createBlock({
      type: 'service',
      description: service.label,
      quantity: 1,
      purchasePrice: service.defaultPurchasePrice,
      salePrice: service.defaultSalePrice,
      origin: {
        source: 'service_catalog',
        productId: service.id,
      },
      position: doc.blocks.length,
    })

    return this.recompute({ ...doc, blocks: [...doc.blocks, block] })
  }

  addStructural(
    doc: QuoteDocumentPro,
    type: Extract<QuoteBlockType, 'title' | 'note' | 'warranty' | 'separator' | 'subtotal' | 'image' | 'discount'>,
    description: string,
    productId: string,
  ): QuoteDocumentPro {
    const block = this.createBlock({
      type,
      description,
      quantity: type === 'discount' ? 1 : 0,
      purchasePrice: 0,
      salePrice: type === 'discount' ? 0 : 0,
      origin: {
        source: 'system',
        productId,
      },
      position: doc.blocks.length,
    })
    return this.recompute({ ...doc, blocks: [...doc.blocks, block] })
  }

  updateBlock(doc: QuoteDocumentPro, blockId: string, patch: Partial<QuoteBlock>): QuoteDocumentPro {
    const blocks = doc.blocks.map((block) => {
      if (block.id !== blockId) return block
      return quoteCalculator.recalculateBlock({
        ...block,
        description: patch.description ?? block.description,
        quantity: patch.quantity ?? block.quantity,
        purchasePrice: patch.purchasePrice ?? block.purchasePrice,
        salePrice: patch.salePrice ?? block.salePrice,
        discountPercent: patch.discountPercent ?? block.discountPercent,
        discountValue: patch.discountValue ?? block.discountValue,
        groupId: patch.groupId ?? block.groupId,
        meta: patch.meta ?? block.meta,
        // origin + id remain immutable
        origin: block.origin,
        id: block.id,
        type: block.type,
        position: block.position,
        margin: block.margin,
        profit: block.profit,
      })
    })
    return this.recompute({ ...doc, blocks })
  }

  removeBlock(doc: QuoteDocumentPro, blockId: string): QuoteDocumentPro {
    return this.recompute({
      ...doc,
      blocks: doc.blocks.filter((b) => b.id !== blockId).map((b, i) => ({ ...b, position: i })),
    })
  }

  duplicateBlock(doc: QuoteDocumentPro, blockId: string): QuoteDocumentPro {
    const source = doc.blocks.find((b) => b.id === blockId)
    if (!source) return doc
    const copy: QuoteBlock = {
      ...source,
      id: crypto.randomUUID(),
      position: doc.blocks.length,
    }
    const validation = quoteValidator.validateBlock(copy)
    if (!validation.valid) throw new Error(validation.errors.join('; '))
    return this.recompute({ ...doc, blocks: [...doc.blocks, copy] })
  }

  moveBlock(doc: QuoteDocumentPro, fromIndex: number, toIndex: number): QuoteDocumentPro {
    if (fromIndex === toIndex) return doc
    const blocks = [...doc.blocks]
    const [item] = blocks.splice(fromIndex, 1)
    if (!item) return doc
    blocks.splice(toIndex, 0, item)
    return this.recompute({
      ...doc,
      blocks: blocks.map((b, i) => ({ ...b, position: i })),
    })
  }

  groupBlocks(doc: QuoteDocumentPro, blockIds: string[], groupId = crypto.randomUUID()): QuoteDocumentPro {
    const blocks = doc.blocks.map((b) =>
      blockIds.includes(b.id) ? { ...b, groupId } : b,
    )
    return this.recompute({ ...doc, blocks })
  }

  private createBlock(input: {
    type: QuoteBlockType
    description: string
    quantity: number
    purchasePrice: number
    salePrice: number
    origin: QuoteBlock['origin']
    position: number
  }): QuoteBlock {
    const block: QuoteBlock = {
      id: crypto.randomUUID(),
      type: input.type,
      origin: input.origin,
      description: input.description,
      quantity: input.quantity,
      purchasePrice: input.purchasePrice,
      salePrice: input.salePrice,
      margin: 0,
      profit: 0,
      discountPercent: 0,
      discountValue: 0,
      position: input.position,
    }
    const calculated = quoteCalculator.recalculateBlock(block)
    const validation = quoteValidator.validateBlock(calculated)
    if (!validation.valid) throw new Error(validation.errors.join('; '))
    return calculated
  }
}

function emptyTotals() {
  return {
    quantity: 0,
    purchaseTotal: 0,
    saleTotal: 0,
    profitTotal: 0,
    marginPercent: 0,
    discountTotal: 0,
    total: 0,
  }
}

export const quoteEngine = new QuoteEngine()

// silence unused import guard for QuoteItemOrigin in some TS configs
export type { QuoteItemOrigin }
